<script setup lang="ts">
/**
 * /terms: the current Terms of Service = the newest version in
 * content/legal/terms/versions.json that is not a draft (the draft while
 * none is published), plus links to every earlier published version.
 * Each version also lives unchanged at /terms/<label> ([version].vue); the
 * API's termsUrl and the acceptance checkboxes link there.
 */
const { public: { siteUrl } } = useRuntimeConfig()

const current = latestPublishedTerms()
const earlier = termsVersions()
  .filter(v => !v.draft && v.label !== current.label)
  .reverse()

const title = 'Terms of Service | Clarify'
const description = 'The Terms of Service for Clarify, the ticket bot for Discord, including the Data Processing Agreement.'
const canonical = `${siteUrl}/terms`

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogUrl: canonical,
  robots: 'index, follow'
})
useHead({ link: [{ rel: 'canonical', href: canonical }] })

const { data } = await useAsyncData('legal-terms', () => loadTermsVersion(current.label))
</script>

<template>
  <LegalDocument
    eyebrow="Nutzungsbedingungen"
    title="Terms of Service"
    lead="The terms for using Clarify, including the Data Processing Agreement."
    :html="data?.html ?? ''"
    :status="termsStatusLine(current)"
    :status-to="current.draft ? undefined : `/terms/${current.label}`"
    :draft="current.draft"
  >
    <section v-if="earlier.length" class="terms-archive" lang="de" aria-labelledby="terms-archive-title">
      <h2 id="terms-archive-title" class="terms-archive-title">Frühere Fassungen</h2>
      <ul class="terms-archive-list">
        <li v-for="v in earlier" :key="v.label">
          <NuxtLink :to="`/terms/${v.label}`" class="nav-link">{{ termsStatusLine(v) }}</NuxtLink>
        </li>
      </ul>
    </section>
  </LegalDocument>
</template>

<style scoped>
.terms-archive {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 76ch;
  margin-top: 22px;
  padding: 26px var(--nb-gutter) 0;
  border-top: 1px solid var(--nb-hairline);
}

.terms-archive-title {
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

.terms-archive-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.98rem;
  color: var(--nb-ink-muted);
}
</style>
